/**
 * First-time-user onboarding helpers.
 *
 * Design notes:
 * - The definitive "done" gate is `agent.onboarding.completedAt` (persisted on
 *   the backend, round-tripped via /agent/login and /agent/get-profile).
 * - We must NOT force pre-existing agents through the wizard. A brand-new agent
 *   has an empty `firstSignIn` on their very first login response (the backend
 *   sets it *after* returning the doc), so that's our "this is a first-timer"
 *   signal. Existing agents already have firstSignIn → never gated.
 * - To survive a refresh mid-flow (after login, firstSignIn is now set), we also
 *   keep a localStorage `onboardingActive` flag. The guard forces the wizard
 *   while that flag is present AND completedAt is still unset.
 */

const ACTIVE_KEY = "onboardingActive";

// Ordered step ids — also defines the total count for the stepper/progress.
export const ONBOARDING_STEPS = ["welcome", "demo", "profile", "tour"];

export const getStoredAgent = () => {
  try {
    const raw = localStorage.getItem("agent");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const getStoredAgency = () => {
  try {
    const raw = localStorage.getItem("travelAgency");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Has this agent finished onboarding?
export const isOnboardingComplete = (agent) => {
  const a = agent || getStoredAgent();
  return !!a?.onboarding?.completedAt;
};

// Is this the agent's first ever login (and not already onboarded)?
// Used at the login moment to decide whether to launch the wizard.
export const isFirstTimeAgent = (agent) => {
  const a = agent || getStoredAgent();
  if (!a) return false;
  if (a?.onboarding?.completedAt) return false;
  return !a.firstSignIn; // empty/undefined on a brand-new agent's first login
};

// Mark the flow as in-progress so a refresh keeps the user gated.
export const startOnboarding = () => localStorage.setItem(ACTIVE_KEY, "1");
export const isOnboardingActive = () => localStorage.getItem(ACTIVE_KEY) === "1";
export const clearOnboardingActive = () => localStorage.removeItem(ACTIVE_KEY);

// The escape-guard's question: should we trap this logged-in agent in the wizard?
export const shouldForceOnboarding = (agent) =>
  isOnboardingActive() && !isOnboardingComplete(agent);

// --- In-app spotlight walkthrough -------------------------------------------
//
// Separate from the chromeless onboarding above. This is the popover-style
// tour that anchors on real sidebar + search controls AFTER the user is in
// the app. Backed by `agent.onboarding.walkthroughSeenAt` (one-shot) with a
// localStorage mirror so a refresh between save and next /me doesn't replay.

const WALKTHROUGH_SEEN_KEY = "walkthroughSeen";

export const isWalkthroughSeen = (agent) => {
  const a = agent || getStoredAgent();
  if (a?.onboarding?.walkthroughSeenAt) return true;
  return localStorage.getItem(WALKTHROUGH_SEEN_KEY) === "1";
};

export const markWalkthroughSeenLocal = () =>
  localStorage.setItem(WALKTHROUGH_SEEN_KEY, "1");

// Show the in-app walkthrough exactly once. Conditions:
// 1. We have a stored agent (i.e. logged in).
// 2. We're NOT mid-chromeless-onboarding (so we don't double-stack the tour on
//    a brand-new user). `shouldForceOnboarding` is true only while the user is
//    actively trapped in /getting-started; it's false both before signup
//    starts AND after they finish — which is the window we want.
// 3. The agent hasn't already seen the walkthrough (server flag or local
//    mirror).
// Legacy agents who never went through the chromeless flow (created before it
// existed, or admins who bypassed it) still get the walkthrough once on their
// next session — that's the whole point of decoupling this from completedAt.
export const shouldShowAppWalkthrough = (agent) => {
  const a = agent || getStoredAgent();
  if (!a) return false;
  if (shouldForceOnboarding(a)) return false;
  return !isWalkthroughSeen(a);
};
